/**
 * chatInput.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
 */

import { DateTime } from "luxon";
import { ObjectId } from "bson";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import { ClassicEditor, Editor, Autoformat, Bold, Italic, Underline, Strikethrough,
    Code, Subscript, Superscript, CodeBlock, BlockQuote, Heading, Image, ImageStyle,
    ImageUpload, Indent, Link, List, Mention, Paragraph, Undo, SpecialCharacters,
    SpecialCharactersEssentials, FileRepository, PasteFromOffice, Widget } from "ckeditor5";

import { SearchIndex } from "emoji-mart";
import * as React from "react";
import { createRoot } from "react-dom/client";
import { flushSync } from "react-dom";

import { CircularProgress, IconButton } from "@mui/material";

import { EmojiMention, AccountMemberMention, MentionCustomization, ImageCustomization } from "../editor/scEditor";

import { ContentType, ContentMessage, MessageType, NotifyUser, NotifyLevel } from "../models/message";


import { UploadFilePlugin, UploadFilePluginUI } from "../editor/uploadFilePlugin";
import { ScUploaderPlugin, SCUploaderAdapterOptions } from "../editor/uploadAdapter";
import { UploadStatusComponent } from "../editor/uploadStatus.component";


interface ChatInputProps {
  groupID: string,
  threadID: string,
  myUserID?: string,
  sendOnReturnKey: boolean,
  getDraft(): Promise<string | null>,
  saveDraft(draft: string): void,
  onSend(msg: ContentMessage, userIDsToNotify: Array<NotifyUser> | undefined): Promise<void>,
  onFocus?(): void,
  getMatchingMembers(queryText: string, groupID: string): Promise<Array<AccountMemberMention>>,
  onError(errMsg: string): void
}

interface ChatInputState {
  sending: boolean,
  uploading: boolean,
  hasInvalidContent: boolean
}

export class ChatInput extends React.Component<ChatInputProps, ChatInputState> {

  private editor?: Editor;
  private unmounting: boolean;
  private loadersInProgress: Set<string>;
  private errantLoaders: Set<string>;
  private domainRegex: RegExp;

  constructor (props: ChatInputProps) {
    super(props);

    this.doSend = this.doSend.bind(this);
    this.uploadEvent = this.uploadEvent.bind(this);
    this.editorReady = this.editorReady.bind(this);
    this.editor = undefined;
    this.unmounting = false;
    this.loadersInProgress = new Set<string>();
    this.errantLoaders = new Set<string>();
    this.domainRegex = new RegExp(`^(https?:)?//(?!${secrets.HOSTNAME})`);
    this.state = {
      sending: false,
      uploading: false,
      hasInvalidContent: false
      //errMsg: ""
    };
  }

  componentDidMount (): void { this.unmounting = false; }
  componentWillUnmount (): void { this.unmounting = true; }

  componentDidUpdate(prevProps: Readonly<ChatInputProps>): void {
    if (prevProps.groupID === this.props.groupID) { return; }
    if ((this.props.threadID || prevProps.threadID) &&
        this.props.threadID === prevProps.threadID) { return; }
    
    if (!this.editor) { return; }
    this.editor.setData("");
    this.props.getDraft().then(draft => {
      if (draft) {
        this.editor?.setData(draft);
      }
    });
  }

  editorReady(editor: ClassicEditor) {
    this.editor = editor;
    (editor as Editor).keystrokes.set("enter", (_data, stop) => {
      if (this.props.sendOnReturnKey) {
        stop();
        this.doSend();
      }
    });
    editor.editing.view.document.on("keydown", () => {
      setTimeout(() => {
        this.saveDraft();
      }, 200);
    });
    editor.keystrokes.set("Ctrl+S", () => {
      this.saveDraft();
    });
    this.props.getDraft().then(draft => {
      if (draft) {
        editor.setData(draft);
      }
    }).catch(err => {
      console.error("Error getting draft:", err);
      this.props.onError((err as Error).message);
    });
  }

  private async searchEmoji(queryText: string) {
    if (!queryText.match(/^[a-zA-Z]/)) {
      queryText = `:${queryText}`;
    }

    const emojis = await SearchIndex.search(queryText) as Array<{skins: Array<{shortcodes: string, native: string}>, name: string}>;
    if (!emojis) { return []; }
    const matches: Array<EmojiMention> = [];
    for (const emoji of emojis) {
      for (const skin of emoji.skins) {
        matches.push({
          id: `${skin.shortcodes} ${skin.native}`,
          _id: skin.shortcodes,
          isEmoji: true,
          name: `${skin.native} ${emoji.name}`,
          text: skin.native
        });
      }
    }
    return matches;
  }

  private saveDraft() {
    if (!this.editor) { return; }
    const content = this.editor.getData();
    this.props.saveDraft(content);
  } 
  
  private async doSend () {
    if (!this.editor) { return; }
    const content: string = this.editor.getData();
    if (this.state.sending || this.state.uploading) { return; }
    if (!content) { return; }
    if (!this.props.myUserID) { return; }

    const mentions: Array<NotifyUser> | undefined = [];
    const mentionsRegex = /data-tag="([\w@!-]+)"/gi;
    let match = mentionsRegex.exec(content);
    while(match !== null) {
      const parts = match[1].split("--");
      const userID = parts[0];
      let level = NotifyLevel.normal;
      let team:undefined|string;
      let teamID:undefined|string;

      if (userID === "team") {
        team = parts[2]
        teamID = parts[3];
      }
      switch (parts[1]) {
        case "@": break;
        case "@!": level = NotifyLevel.elevated; break;
        case "@!!": level = NotifyLevel.urgent; break;
        case "@-": level = NotifyLevel.silent; break;
        default: break;
      }
      mentions.push({ userID, level, team: team, teamID: teamID });
      match = mentionsRegex.exec(content);
    }

    const LOCK = "while-sending";
    try {
      this.setState({sending: true});
      this.editor.enableReadOnlyMode(LOCK);

      const msg: ContentMessage = {
        _id: new ObjectId().toString(),
        groupID: this.props.groupID,
        userID: this.props.myUserID,
        threadID: this.props.threadID || "",
        msgtype: MessageType.CONTENT,
        content: content,
        contenttype: ContentType.html,
        replyCount: 0,
        created: DateTime.now()
      }

      await this.props.onSend(msg, mentions);

      if (!this.unmounting) {
        const plugin = this.editor.plugins.get("UploadFilePlugin") as UploadFilePlugin;
        plugin.submitted = true;
        this.editor.setData("");
        this.editor.disableReadOnlyMode(LOCK);
        this.editor.editing.view.focus();
      }
    } catch (err) {
      console.error("Error sending msg:", err);
      this.props.onError((err as Error).message);
    } finally {
      if (!this.unmounting) {
        this.setState({sending: false});
        this.editor.disableReadOnlyMode(LOCK);
      }
    }
  }

  private renderUploadStatus(domElement: HTMLElement,
      uploadStatus: "idle" | "uploading" | "complete" | "final" | "invalid",
      uploadPercent: number) {
    const root = createRoot(domElement);
    if (uploadStatus == "final") {
      // update immediately when sending the message
      flushSync(() => {root.render(
        <UploadStatusComponent
          status={uploadStatus}
          percent={uploadPercent} />
      )});
    }
    else {
      root.render(
        <UploadStatusComponent
          status={uploadStatus}
          percent={uploadPercent} />
      );
    }
  }

  private uploadEvent(eventName: "start" | "complete" | "error" | "removed", uploadID: string, msg: string) {
    console.log("upload event:", eventName, uploadID, this.loadersInProgress,
      "uploading state:", this.state.uploading, "errant:", this.errantLoaders);
    if (eventName === "start") {
      this.loadersInProgress.add(uploadID);
    } else if ((eventName === "complete" || eventName === "removed") && uploadID) {
      this.loadersInProgress.delete(uploadID);
      this.errantLoaders.delete(uploadID);
    } else if (eventName === "error" && uploadID) {
      this.loadersInProgress.delete(uploadID);
      this.errantLoaders.add(uploadID);
      if (msg) { this.props.onError(msg); }
    }
    if (!this.state.uploading && this.loadersInProgress.size > 0) {
      this.setState({uploading: true});
    } else if (this.state.uploading && this.loadersInProgress.size <= 0) {
      this.setState({uploading: false});
    }
    this.setState({hasInvalidContent: this.errantLoaders.size > 0});
  }

  render(): React.ReactElement<any, any> {
    const editorConfig = {
      licenseKey: "GPL",
      placeholder: "Type message...",
      plugins: [
        Autoformat,
        Bold,
        Italic,
        Underline,
        Strikethrough,
        Code,
        Subscript,
        Superscript,
        CodeBlock,
        BlockQuote,
        Heading,
        Image,
        ImageStyle,
        ImageUpload,
        Indent,
        Link,
        List,
        Mention,
        Paragraph,
        Undo,
        SpecialCharacters,
        SpecialCharactersEssentials,
        FileRepository,
        PasteFromOffice,
        Widget,

        ScUploaderPlugin,
        UploadFilePlugin,
        UploadFilePluginUI,
        MentionCustomization as any,
        ImageCustomization as any
      ],
      toolbar: {
        items: [
          "heading", "|",
          "bold", "italic", "underline",
          "strikethrough",
          "|",
          "link",
          "uploadImage",
          "uploadFile",
          "|",
          "bulletedList", "numberedList",
          //"fontfamily", "fontsize", "fontColor", "fontBackgroundColor", "|",
          "codeBlock", "|",
          "outdent", "indent", "|",
          "blockQuote",
          //"specialCharacters",
          "subscript", "superscript",
          "undo", "redo"
        ]
      },
      codeBlock: {
        indentSequence: "  ",
        languages: [
          {language: "plaintext", label: "Plain text"},
          {language: "c", label: "C"},
          {language: "c#", label: "C#"},
          {language: "c++", label: "C++"},
          {language: "css", label: "CSS"},
          {language: "diff", label: "Diff"},
          {language: "html", label: "HTML"},
          {language: "java", label: "Java"},
          {language: "js", label: "Javascript"},
          {language: "php", label: "PHP"},
          {language: "py", label: "Python"},
          {language: "ruby", label: "Ruby"},
          {language: "ts", label: "TypeScript"},
          {language: "xml", label: "XML"}
        ]
      },
      link: {
        defaultProtocol: "http://",
        decorators: {
          all: {
            mode: "automatic",
            callback: (url: string | null): boolean => {
              if (!url) { return false; }
              return this.domainRegex.test(url);
            },
            attributes: {
              target: "_blank",
              rel: "noopener noreferrer"
            }
          }
        } as any
      },
      language: "en",
      mention: {
        feeds: [{
          marker: "@",
          feed: (queryText: string) => this.props.getMatchingMembers(queryText, this.props.groupID)
        }, {
          marker: ":",
          feed: (queryText: string) => this.searchEmoji(queryText)
        }]
      },
      scUpload: {
        groupID: () => this.props.groupID,
        statusRenderer: this.renderUploadStatus,
        uploadEvent: this.uploadEvent
      } as SCUploaderAdapterOptions
    };
    return <div className="group-message-input-container">
      <CKEditor editor={ClassicEditor}
        config={editorConfig}
        onFocus={() => {
          this.props.onFocus && this.props.onFocus();
        }}
        onReady={this.editorReady} />
      <IconButton size="small" className="send-btn" color="primary"
          disableRipple disableTouchRipple aria-label="Send"
          onClick={this.doSend} disabled={this.state.hasInvalidContent}>
        {this.state.sending || this.state.uploading ?
          <CircularProgress size={20} /> :
          <div className="send-btn-icon" />}
      </IconButton>
    </div>;
  }
}
